import { z } from 'zod/v4';

export const UpdateUserParamsSchema = z.object({
  id: z.string().min(1),
});

export type UpdateUserParams = z.infer<typeof UpdateUserParamsSchema>;

/**
 * Faithful port of the legacy `user.update` JSON Schema
 * (`backend/src/2025-06/api-service/routes/schemas/v3/request/user.js`).
 * Every field is optional — the legacy schema has no `required` array.
 *
 * `hasAgreedWithTerms` is intentionally dropped: the legacy handler computes
 * it but `usersDB.update`'s destructured params never include it, so it was
 * never actually persisted — accepted-but-ignored noise, not a real field.
 * `developmentTag` was never in the legacy schema either (only the admin
 * PATCH endpoint can set it).
 */
export const UpdateUserBodySchema = z.object({
  firstName: z
    .string()
    .min(1)
    .max(255)
    .regex(/^\S.*/, 'firstName must not start with whitespace')
    .optional(),
  lastName: z.string().min(1).nullable().optional(),
  langId: z.enum(['en', 'es', 'de', 'fr']).optional(),
  isBlocked: z.boolean().nullable().optional(),
  /**
   * Lets Retraced staff pick the company the pre-update existing-user lookup is
   * linked through — NOT persisted anywhere. Ignored for everyone else, whose
   * lookup is always anchored on their own company (a company admin must not be
   * able to reach another tenant's users by naming it here).
   */
  companyId: z.string().min(1).nullable().optional(),
});

export type UpdateUserBody = z.infer<typeof UpdateUserBodySchema>;
